import i18n from '@/utils/i18n';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AccountStatus } from './account-status';
import { ActionToastContent } from '@/components/action-toast-content';
import { ActivityIndicator } from '@/components/activity-indicator';
import { AnalyticEvent } from '@/analytics/fennel/registry';
import { Analytics } from '@/utils/analytics';
import { AnalyticsAlertResponse } from '@/components/dsr-analytics/types';
import { AnalyticsAlerts } from './analytics-alerts';
import {
  AnalyticsAlertStatusType,
  AnalyticsAlertType,
  Locations_AccountRelationshipFieldsFragment,
} from '@/types';
import { ChatStatus, TrackableComponent } from '@/enums';
import { createStyles } from '@/contexts/theme-context';
import { fennelTrack } from '@/analytics/fennel/hook';
import { formatCurrency, MICRO_CONVERSION } from '@/utils/money';
import {
  getAlertUUIDs,
  useSalesAlerts,
} from '@/hooks/use-analytics-alerts-data';
import { HorizontalBar } from '@/components/dsr-analytics/horizontal-bar';
import { ItemDetailsActionSheet } from '@/components/items/item-details-action-sheet';
import { Platform, Text, View } from 'react-native';
import { TimeRange } from './types';
import { TotalSalesGraph } from '@/components/dsr-analytics/total-sales-graph';
import { TotalSalesTable } from './total-sales-table';
import { useHubCardNavigation } from '@/hooks/insights-hub/use-hub-card-navigation';
import { useSalesAnalyticsData } from '@/hooks/use-sales-analytics-data';
import { useSupplierCurrencyCode } from '@/hooks/use-supplier-currency-code';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';

interface Props {
  chatUUID: string;
  supplierUUID: string;
}

interface AnalyticsPanelProps {
  chat: Locations_AccountRelationshipFieldsFragment;
  supplierUUID: string;
}

export const AnalyticsPanel: React.FC<AnalyticsPanelProps> = ({
  chat,
  supplierUUID,
}) => {
  const chatUUID = chat.uuid;
  const { chatRestaurantMap, restaurantUUIDToRelationshipMap } =
    useUserContext();
  const restaurant =
    restaurantUUIDToRelationshipMap[chatRestaurantMap[chatUUID]];
  const { chatStatus } = restaurant;

  const { navigateToItemDetails } = useHubCardNavigation();

  const [variantPackUUID, setVariantPackUUID] = useState<string | null>(null);
  const [showActionSheet, setShowActionSheet] = useState(false);

  const { showErrorToast, showToast } = useToasts();
  const getActionToastText = (actionType: AnalyticsAlertStatusType) => {
    switch (actionType) {
      case AnalyticsAlertStatusType.Hidden:
        return i18n.t('dsr_analytics.alert_hidden');
      case AnalyticsAlertStatusType.Delete:
        return i18n.t('dsr_analytics.alert_removed');
      default:
        return '';
    }
  };

  const { alerts, updateAlertStatuses } = useSalesAlerts(
    { chatUUID },
    supplierUUID,
  );

  const trackAlertAction = useCallback(
    (alert: AnalyticsAlertResponse, actionType: AnalyticsAlertStatusType) => {
      const alertUUIDs = getAlertUUIDs(alert);
      const trackingData = {
        alertType: alerts[alertUUIDs[0]]?.alert_type,
        alertUUIDs,
        restaurantUUID: restaurant.uuid,
      };

      switch (actionType) {
        case AnalyticsAlertStatusType.Hidden:
          Analytics.track(
            'account_details.analytics_panel.action_sheet.hide.press',
            trackingData,
          );
          break;
        case AnalyticsAlertStatusType.Delete:
          Analytics.track(
            'account_details.analytics_panel.action_sheet.delete.press',
            trackingData,
          );
          break;
        default:
          break;
      }
    },
    [alerts, restaurant.uuid],
  );

  const trackUndoAlertAction = useCallback(
    (alert: AnalyticsAlertResponse, actionType: AnalyticsAlertStatusType) => {
      const alertUUIDs = getAlertUUIDs(alert);
      const trackingData = {
        alertType: alerts[alertUUIDs[0]]?.alert_type,
        alertUUIDs,
        restaurantUUID: restaurant.uuid,
      };

      switch (actionType) {
        case AnalyticsAlertStatusType.Hidden:
          Analytics.track(
            'account_details.analytics_panel.action_sheet.hide.undo.press',
            trackingData,
          );
          break;
        case AnalyticsAlertStatusType.Delete:
          Analytics.track(
            'account_details.analytics_panel.action_sheet.delete.undo.press',
            trackingData,
          );
          break;
        default:
          break;
      }
    },
    [alerts, restaurant.uuid],
  );

  const updateAlertStatus = useCallback(
    async (
      alert: AnalyticsAlertResponse,
      actionType: AnalyticsAlertStatusType,
    ) => {
      try {
        // update alert status
        await updateAlertStatuses(alert, actionType);
        trackAlertAction(alert, actionType);

        // show confirmation toast with undo button
        showToast({
          autoCloseTimeout: 0,
          content: (onClose) => (
            <ActionToastContent
              buttonProps={{
                children: i18n.t('undo'),
                onPress: async () => {
                  onClose();

                  // undo alert action
                  await updateAlertStatuses(
                    alert,
                    AnalyticsAlertStatusType.Active,
                  );
                  trackUndoAlertAction(alert, actionType);
                },
                trackingData: null,
              }}
              textContent={getActionToastText(actionType)}
            />
          ),
        });
      } catch (error) {
        showErrorToast();
      }
    },
    [
      updateAlertStatuses,
      trackAlertAction,
      trackUndoAlertAction,
      showErrorToast,
      showToast,
    ],
  );

  const onSupportInfoPress = useCallback(
    (alert: AnalyticsAlertResponse) => {
      if (alert.alert_type === AnalyticsAlertType.LostItem) {
        const alertUUID = alert.alert_uuid;
        const restaurantUUID = alert.restaurant_uuid;
        const supplierUUID = alert.supplier_uuid;
        const variantPackUUID = alert.variant_pack_uuid;

        if (Platform.OS === 'web') {
          setVariantPackUUID(variantPackUUID);
          setShowActionSheet(true);
        } else {
          navigateToItemDetails(
            restaurantUUID,
            supplierUUID,
            variantPackUUID,
            TrackableComponent.ANALYTICS_ACTION_SHEET,
          );
        }

        fennelTrack(AnalyticEvent.SALES_ALERT_LOST_ITEM_ACTION_SHEET_PRESS, {
          alertUUID,
          restaurantUUID,
          supplierUUID,
          variantPackUUID,
        });
      }
    },
    [navigateToItemDetails],
  );

  const shouldShowTotalSalesDashboard =
    restaurant?.showTotalSalesDashboard &&
    [ChatStatus.ACTIVE, ChatStatus.NEW].includes(chatStatus);

  const uniqueAlertTypes = [
    ...new Set(
      Object.values(alerts).map((alertResponse) => alertResponse!.alert_type),
    ),
  ];

  return (
    <>
      <AccountStatus alertTypes={uniqueAlertTypes} chat={chat} />
      {Object.entries(alerts).length > 0 ? (
        <AnalyticsAlerts
          alerts={alerts}
          onAction={updateAlertStatus}
          onSupportInfoPress={onSupportInfoPress}
        />
      ) : null}
      <HorizontalBar />
      {shouldShowTotalSalesDashboard ? (
        <TotalSales chatUUID={chatUUID} supplierUUID={supplierUUID} />
      ) : null}
      {variantPackUUID ? (
        <ItemDetailsActionSheet
          origin={TrackableComponent.ANALYTICS_ACTION_SHEET}
          restaurantUUID={restaurant.uuid}
          setShowActionSheet={(show) => {
            setShowActionSheet(show);
            setVariantPackUUID(null);
          }}
          showActionSheet={showActionSheet}
          supplierUUID={supplierUUID}
          variantPackUUID={variantPackUUID}
        />
      ) : null}
    </>
  );
};

const TotalSales: React.FC<Props> = ({ chatUUID, supplierUUID }) => {
  const [styles] = useStyles();
  const [range, setTimeRange] = useState(TimeRange.L30);
  const { showErrorToast } = useToasts();

  const endDate = useRef(new Date());

  const startDate = useMemo(() => {
    return new Date(endDate.current.getTime() - range * 24 * 60 * 60 * 1000);
  }, [range, endDate]);

  const queryVariables = useMemo(
    () => ({
      chatUUID: chatUUID,
      endTime: endDate.current.toISOString(),
      startTime: startDate.toISOString(),
    }),
    [endDate, startDate, chatUUID],
  );

  const { data, error, loading } = useSalesAnalyticsData(
    queryVariables,
    supplierUUID,
  );
  const { currencyCode: supplierCurrencyCode } =
    useSupplierCurrencyCode(supplierUUID);

  const transformedData = useMemo(() => {
    if (data) {
      const { totalSalesData, ...tableData } = data;
      const graphData = totalSalesData.map(
        ({ date, totalSalesAmountInMicros }) => ({
          label: formatCurrency(totalSalesAmountInMicros, supplierCurrencyCode),
          x: date,
          y: +(totalSalesAmountInMicros / MICRO_CONVERSION).toFixed(2),
        }),
      );

      return { graphData, tableData };
    }
  }, [data, supplierCurrencyCode]);

  useEffect(() => {
    if (error) {
      showErrorToast();
    }
  }, [error, showErrorToast]);

  return (
    <>
      <View style={styles.timeRangeContainer}>
        <View
          style={range === TimeRange.L7 ? styles.selectedChoice : styles.choice}
        >
          <Text
            onPress={() => setTimeRange(TimeRange.L7)}
            style={range === TimeRange.L7 ? styles.selectedText : styles.text}
          >
            L7
          </Text>
        </View>
        <View
          style={
            range === TimeRange.L30 ? styles.selectedChoice : styles.choice
          }
        >
          <Text
            onPress={() => setTimeRange(TimeRange.L30)}
            style={range === TimeRange.L30 ? styles.selectedText : styles.text}
          >
            L30
          </Text>
        </View>
        <View
          style={
            range === TimeRange.L60 ? styles.selectedChoice : styles.choice
          }
        >
          <Text
            onPress={() => setTimeRange(TimeRange.L60)}
            style={range === TimeRange.L60 ? styles.selectedText : styles.text}
          >
            L60
          </Text>
        </View>
      </View>
      {loading ? (
        <View style={styles.loadingIndicator}>
          <ActivityIndicator />
        </View>
      ) : null}
      {transformedData?.graphData ? (
        <TotalSalesGraph
          salesData={transformedData.graphData}
          timePeriod={range}
        />
      ) : null}
      {transformedData?.tableData ? (
        <TotalSalesTable data={transformedData.tableData} />
      ) : null}
      <HorizontalBar />
    </>
  );
};

const useStyles = createStyles((theme) => ({
  choice: {
    alignSelf: 'flex-start',
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.stroke,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 1,
  },
  loadingIndicator: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    paddingVertical: 200,
  },
  selectedChoice: {
    alignSelf: 'flex-start',
    backgroundColor: theme.colors.accent,
    borderColor: theme.colors.accent,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 1,
  },
  selectedText: {
    color: theme.colors.white,
    fontFamily: theme.fonts.primarySemiBold,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  text: {
    color: theme.colors.primary,
    fontFamily: theme.fonts.primarySemiBold,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  timeRangeContainer: {
    flexDirection: 'row',
    gap: 8,
    paddingHorizontal: 24,
    paddingTop: 24,
  },
}));
